// PrechartingTab.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import { Badge } from '../../ui/badge';
import { 
  Heart, 
  Stethoscope, 
  Activity, 
  User, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  FileText,
  Lock,
  Pill
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import ProblemListForm from './problem-list-form';
import VitalsLabsForm from './vitals-labs-form';
import DiagnosticImagingForm from './diagnostic-imaging-form';
import ProceduresForm from './procedures-form';
import SpecialistsVisitsForm from './specialists-visits-form';
import MedicationListForm from './medication-list-form';
import { prechartingService } from '../../../apis/prechartingService';
import { DottedLoader } from '../../ui/loading-spinner';

const PrechartingTab = ({ sessionId, patientContext, onDataChange, readonly = false }) => {
  const AUTOSAVE_DEBOUNCE_MS = 800;
  const [activeSection, setActiveSection] = useState('problems');
  const [prechartingData, setPrechartingData] = useState({
    problemList: null,
    vitalsLabs: null,
    diagnosticImaging: null,
    procedures: null,
    specialistsVisits: null,
    medicationList: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState(null);
  const autosaveTimerRef = useRef(null);

  useEffect(() => {
  // Skip demo session
  if (!sessionId || sessionId === "demo-session") return;
  loadPrechartingData();
}, [sessionId]);

  const loadPrechartingData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await prechartingService.getPrechartingData(sessionId);
      
      if (response.success && response.data) {
        setPrechartingData({
          problemList: response.data.problemList || null,
          vitalsLabs: response.data.vitalsLabs || null,
          diagnosticImaging: response.data.diagnosticImaging || null,
          procedures: response.data.procedures || null,
          specialistsVisits: response.data.specialistsVisits || null,
          medicationList: response.data.medicationList || null
        });
        setLastSaved(response.data.updatedAt);
      } else if (response.message?.includes('not found')) {
        console.log('No precharting data found for this session');
      } else {
        throw new Error(response.message || 'Failed to load precharting data');
      }
    } catch (error) {
      console.error('Error loading precharting data:', error);
      setError('Failed to load precharting data. Please try again.');
      toast.error('Failed to load precharting data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSectionDataChange = useCallback((section, data) => {
    setPrechartingData(prev => {
      const updatedData = {
        ...prev,
        [section]: data
      };
      
      if (onDataChange) {
        onDataChange(updatedData);
      }
      
      return updatedData;
    });
    setHasUnsavedChanges(true);
  }, []);

  const saveAllData = useCallback(async (silent = false) => {
    try {
      setIsSaving(true);
      
      const response = await prechartingService.createOrUpdatePrechartingData(sessionId, prechartingData);
      
      if (response.success) {
        if (!silent) {
          toast.success('Precharting data saved successfully');
        }
        setHasUnsavedChanges(false);
        setLastSaved(new Date().toISOString());
        return true;
      } else {
        throw new Error(response.message || 'Failed to save data');
      }
    } catch (error) {
      console.error('Error saving all precharting data:', error);
      toast.error('Failed to auto-save precharting data');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [sessionId, prechartingData]);

  useEffect(() => {
    if (!sessionId || readonly || !hasUnsavedChanges) return;

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null;
      void saveAllData(true);
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [sessionId, readonly, hasUnsavedChanges, prechartingData, saveAllData]);

  const handleCompletePrecharting = async () => {
    try {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
      const saved = await saveAllData(true);
      if (!saved) return;
      const response = await prechartingService.completePrechartingData(sessionId);
      
      if (response.success) {
        toast.success('Precharting data marked as completed');
        setLastSaved(new Date().toISOString());
      } else {
        throw new Error(response.message || 'Failed to complete precharting');
      }
    } catch (error) {
      console.error('Error completing precharting:', error);
      toast.error('Failed to complete precharting data');
    }
  };

  const getSectionStatus = (section) => {
    const data = prechartingData[section];
    if (!data) return 'empty';
    
    if (Array.isArray(data)) {
      return data.length > 0 ? 'filled' : 'empty';
    }
    
    if (typeof data === 'object') {
      const hasData = Object.values(data).some(value => 
        value !== null && value !== undefined && value !== ''
      );
      return hasData ? 'filled' : 'empty';
    }
    
    return 'empty';
  };

  const getCompletionPercentage = () => {
    const sections = ['problemList', 'vitalsLabs', 'diagnosticImaging', 'procedures', 'specialistsVisits', 'medicationList'];
    const filledSections = sections.filter(section => getSectionStatus(section) === 'filled');
    return Math.round((filledSections.length / sections.length) * 100);
  };

  const completionPercentage = getCompletionPercentage();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2 text-gray-700">
          <DottedLoader size="sm" />
          <span>Loading precharting data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6"> 
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-500" />
            Precharting Information
            {readonly && (
              <Badge variant="outline" className="text-gray-500 border-gray-300">
                <Lock className="h-3 w-3 mr-1" />
                Read Only
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                Add patient information to enhance SOAP note generation quality
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Completion:</span>
                <Badge variant={completionPercentage === 100 ? "default" : "secondary"}>
                  {completionPercentage}%
                </Badge>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {hasUnsavedChanges ? (
                <Badge variant="outline" className="text-orange-600 border-orange-600">
                  {isSaving ? 'Auto-saving...' : 'Saving changes...'}
                </Badge>
              ) : (
                lastSaved && (
                  <Badge variant="outline" className="text-green-600 border-green-600">
                    Auto-saved
                  </Badge>
                )
              )}
              {lastSaved && (
                <span className="text-xs text-gray-500">
                  Last saved: {new Date(lastSaved).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Tabs value={activeSection} onValueChange={setActiveSection} className="w-full">
        <TabsList className="flex flex-wrap h-auto w-full justify-start gap-1.5 rounded-2xl p-1.5 xl:grid xl:grid-cols-6">
          <TabsTrigger value="problems" className="flex grow items-center justify-center gap-2 rounded-full px-4 py-2 xl:grow-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:[&_.status-check]:text-white">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline ">ICD-10 Codes</span>
            <span className="sm:hidden">Codes</span>
            {getSectionStatus('problemList') === 'filled' && (
              <CheckCircle className="status-check h-3 w-3 text-green-500" />
            )}
          </TabsTrigger>
          <TabsTrigger value="medications" className="flex grow items-center justify-center gap-2 rounded-full px-4 py-2 xl:grow-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:[&_.status-check]:text-white">
            <Pill className="h-4 w-4" />
            <span className="hidden sm:inline">Medications</span>
            <span className="sm:hidden">Meds</span>
            {getSectionStatus('medicationList') === 'filled' && (
              <CheckCircle className="status-check h-3 w-3 text-green-500" />
            )}
          </TabsTrigger>
          <TabsTrigger value="vitals" className="flex grow items-center justify-center gap-2 rounded-full px-4 py-2 xl:grow-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:[&_.status-check]:text-white">
            <Heart className="h-4 w-4" />
            <span className="hidden sm:inline">Vitals & Labs</span>
            <span className="sm:hidden">Vitals</span>
            {getSectionStatus('vitalsLabs') === 'filled' && (
              <CheckCircle className="status-check h-3 w-3 text-green-500" />
            )}
          </TabsTrigger>
          <TabsTrigger value="imaging" className="flex grow items-center justify-center gap-2 rounded-full px-4 py-2 xl:grow-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:[&_.status-check]:text-white">
            <Stethoscope className="h-4 w-4" />
            <span className="hidden sm:inline">Diagnostic Imaging</span>
            <span className="sm:hidden">Imaging</span>
            {getSectionStatus('diagnosticImaging') === 'filled' && (
              <CheckCircle className="status-check h-3 w-3 text-green-500" />
            )}
          </TabsTrigger>
          <TabsTrigger value="procedures" className="flex grow items-center justify-center gap-2 rounded-full px-4 py-2 xl:grow-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:[&_.status-check]:text-white">
            <Activity className="h-4 w-4" />
            <span className="hidden sm:inline">Procedures</span>
            <span className="sm:hidden">Procedures</span>
            {getSectionStatus('procedures') === 'filled' && (
              <CheckCircle className="status-check h-3 w-3 text-green-500" />
            )}
          </TabsTrigger>
          <TabsTrigger value="specialists" className="flex grow items-center justify-center gap-2 rounded-full px-4 py-2 xl:grow-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:[&_.status-check]:text-white">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">Specialists</span>
            <span className="sm:hidden">Specialists</span>
            {getSectionStatus('specialistsVisits') === 'filled' && (
              <CheckCircle className="status-check h-3 w-3 text-green-500" />
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="problems" className="mt-6">
          <ProblemListForm
            data={prechartingData.problemList}
            onChange={(data) => handleSectionDataChange('problemList', data)}
            isLoading={isSaving}
            readonly={readonly}
          />
        </TabsContent>

        <TabsContent value="medications" className="mt-6">
          <MedicationListForm
            data={prechartingData.medicationList}
            onChange={(data) => handleSectionDataChange('medicationList', data)}
            isLoading={isSaving}
            readonly={readonly}
          />
        </TabsContent>

        <TabsContent value="vitals" className="mt-6">
          <VitalsLabsForm
            data={prechartingData.vitalsLabs}
            onChange={(data) => handleSectionDataChange('vitalsLabs', data)}
            isLoading={isSaving}
            readonly={readonly}
          />
        </TabsContent>

        <TabsContent value="imaging" className="mt-6">
          <DiagnosticImagingForm
            data={prechartingData.diagnosticImaging}
            onChange={(data) => handleSectionDataChange('diagnosticImaging', data)}
            isLoading={isSaving}
            readonly={readonly}
          />
        </TabsContent>

        <TabsContent value="procedures" className="mt-6">
          <ProceduresForm
            data={prechartingData.procedures}
            onChange={(data) => handleSectionDataChange('procedures', data)}
            isLoading={isSaving}
            readonly={readonly}
          />
        </TabsContent>

        <TabsContent value="specialists" className="mt-6">
          <SpecialistsVisitsForm
            data={prechartingData.specialistsVisits}
            onChange={(data) => handleSectionDataChange('specialistsVisits', data)}
            isLoading={isSaving}
            readonly={readonly}
          />
        </TabsContent>
      </Tabs>
      <div className="flex justify-between items-center pt-4 border-t">
        <div className="text-sm text-gray-500">
          {completionPercentage === 100 
            ? "All sections completed!" 
            : `${completionPercentage}% complete - ${6 - Math.floor(completionPercentage / (100/6))} sections remaining`
          }
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleCompletePrecharting}
            disabled={isSaving || completionPercentage < 100 || readonly}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Complete Precharting
          </Button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(PrechartingTab);
